import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, ShoppingCart, ArrowDownRight, FileText } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';

interface ExpenseItem {
  id: string | number;
  description: string;
  amount: number;
  date: string; // ISO date string
}

interface PurchasesDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchasesData: {
    totalPurchases: number;
    supplierInvoicesTotal: number;
    expensesTotal: number;
  };
  period: string;
  /** Gastos individuales que se muestran en el detalle */
  expenses: ExpenseItem[];
}

const PurchasesDetailModal: React.FC<PurchasesDetailModalProps> = ({
  isOpen,
  onClose,
  purchasesData,
  period,
  expenses = []
}) => {
  // Traduce el filtro de período a texto legible
  const getPeriodDisplay = (period: string) => {
    switch (period) {
      case 'month':
        return 'este mes';
      case 'month-1':
        return 'mes anterior';
      case 'month-2':
        return 'hace 2 meses';
      case 'month-3':
        return 'hace 3 meses';
      case 'month-4':
        return 'hace 4 meses';
      case 'quarter':
        return 'este trimestre';
      case 'year':
        return 'este año';
      case 'custom':
        return 'período seleccionado';
      default:
        return 'todo el tiempo';
    }
  };

  // Calcula el porcentaje que representa un valor sobre el total de compras
  const getPercentage = (value: number) => {
    if (purchasesData.totalPurchases === 0) return 0;
    return Math.round((value / purchasesData.totalPurchases) * 100);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 max-h-[85vh] w-[90vw] max-w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg overflow-y-auto">
          {/* Título */}
          <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
            <ShoppingCart className="mr-2 h-5 w-5 text-red-600 dark:text-red-400" />
            Detalle de Compras ({getPeriodDisplay(period)})
          </Dialog.Title>

          {/* Resumen */}
          <div className="space-y-6">
            {/* Compras Totales */}
            <div className="bg-red-50 dark:bg-red-900/30 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-red-700 dark:text-red-300">Compras Totales</p>
                  <p className="text-2xl font-bold text-red-800 dark:text-red-200">
                    {formatCurrency(purchasesData.totalPurchases)}
                  </p>
                </div>
                <div className="p-3 bg-red-100 dark:bg-red-800 rounded-full">
                  <ArrowDownRight className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
            </div>

            {/* Desglose */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Desglose de Compras</h3>

              {/* Facturas de proveedores */}
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Facturas de Proveedores</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(purchasesData.supplierInvoicesTotal)}
                    </p>
                  </div>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {getPercentage(purchasesData.supplierInvoicesTotal)}%
                  </div>
                </div>
                <div className="mt-2 w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div
                    className="bg-red-600 dark:bg-red-500 h-2 rounded-full"
                    style={{ width: `${getPercentage(purchasesData.supplierInvoicesTotal)}%` }}
                  ></div>
                </div>
              </div>

              {/* Gastos generales */}
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Gastos Generales</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(purchasesData.expensesTotal)}
                    </p>
                  </div>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {getPercentage(purchasesData.expensesTotal)}%
                  </div>
                </div>
                <div className="mt-2 w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div
                    className="bg-orange-600 dark:bg-orange-500 h-2 rounded-full"
                    style={{ width: `${getPercentage(purchasesData.expensesTotal)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Botón Cerrar */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            >
              Cerrar
            </button>
          </div>

          {/* Ícono X de cierre */}
          <Dialog.Close asChild>
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              aria-label="Cerrar"
            >
              <X className="h-6 w-6" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default PurchasesDetailModal;